import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useUser } from './UserContext';

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Get the initial theme from localStorage, defaulting to 'dark'
  const { user, isAuthenticated } = useUser();
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme === 'light' ? 'light' : 'dark') as Theme;
  });

  // Load user theme preference from Supabase when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      const loadUserTheme = async () => {
        try {
          const { data, error } = await supabase
            .from('users')
            .select('light_mode')
            .eq('id', user.id)
            .single();
            
          if (error) throw error;
          
          if (data && data.light_mode !== null) {
            // Set theme based on user preference in database
            setTheme(data.light_mode ? 'light' : 'dark');
            // Also update localStorage to match
            localStorage.setItem('theme', data.light_mode ? 'light' : 'dark');
          }
        } catch (err) {
          console.error('Error loading user theme preference:', err);
        }
      };
      
      loadUserTheme();
    }
  }, [isAuthenticated, user]);

  // Update the document with the current theme
  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme', theme);
    
    // Update the HTML element with the theme
    const htmlElement = document.documentElement;
    if (theme === 'light') {
      htmlElement.classList.add('light-mode');
      htmlElement.classList.remove('dark-mode');
    } else {
      htmlElement.classList.add('dark-mode');
      htmlElement.classList.remove('light-mode');
    }
  }, [theme]);

  // Toggle between light and dark themes
  const toggleTheme = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    
    // Update local state and localStorage
    setTheme(prevTheme => (prevTheme === 'dark' ? 'light' : 'dark'));
    
    // If user is authenticated, save preference to database
    if (isAuthenticated && user) {
      try {
        // Save the theme preference to Supabase (light_mode = true for light theme)
        const { error } = await supabase
          .from('users')
          .update({ light_mode: newTheme === 'light' })
          .eq('id', user.id);
          
        if (error) throw error;
        
      } catch (err) {
        console.error('Error saving theme preference:', err);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}